/*
 * Copyright (c) 2022. American Express Company
 */

import $ from 'jquery';
import { LocalDate } from '@js-joda/core';
import React from 'react';
import { ButtonSecondary, IconDeclined, IconSearch, IconWarning } from '@americanexpress/dls-react';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import Typography from 'src/components/typography/Typography';

import CardService from 'src/services/card.service';
import FormatService from 'src/services/format.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import { AccountModel } from 'src/types/account.model';
import { BudgetModel } from 'src/types/budget.model';
import { CardModel } from 'src/types/card.model';
import Card from 'src/components/atomic/atoms/card/Card';
import Divider from 'src/components/atomic/atoms/divider/Divider';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import vars from 'src/styles/variables.module.scss';

import { props, sectionProps } from 'src/types/view-models/planActions.view.model';
import bgColors from 'src/styles/bgColors.module.scss';
import styles from './planActions.module.scss';
import { goalIcons } from 'src/bw/goal/Goal';
import PlanService from 'src/services/plan.service';
import Selectors from 'src/utils/selectors';

const Section = ({ id, icon, button, children, className, 'data-cy': dataCy }: sectionProps): JSX.Element => (
    <>
        <Divider />
        <div id={id} className={styles.section} data-cy={dataCy}>
            {icon}
            <div className={`${styles.sectionContainer} ${className}`}>
                <div className={styles.bodyContainer}>{children}</div>
                {button}
            </div>
        </div>
    </>
);

const renderInstitutionWarnings: Function = (accounts: AccountModel[], goTo: (...args: any[]) => any) => {
    return accounts
        .filter((a) => a.institution.loginRequired)
        .map((a, i) => {
            const onClick = (): void => goTo(`/drawer/account/${a.accountId}`);
            return (
                <Section
                    data-cy={Selectors.PlanAhead.PLAN_ACTIONS_INSTITUTION_WARNINGS}
                    key={`institutionWarning${i}`}
                    icon={
                        <Circle className={bgColors.badWeak}>
                            <IconDeclined size="md" style={{ fill: vars.badStrong }} />
                        </Circle>
                    }
                    button={
                        <ButtonSecondary className={styles.button} onClick={onClick}>
                            {LiteralsService.getLiteral('Insights.planActions.fixIt')}
                        </ButtonSecondary>
                    }
                >
                    <Typography variant={TypographyVariants.BODY_BOLD}>
                        <Anchor onClick={onClick}>{a.name}</Anchor>
                        {LiteralsService.getLiteral('Insights.planActions.accountReconnected')}
                    </Typography>
                </Section>
            );
        });
};

export default function PlanActions({
    user,
    viewBudgetTransactionsOnClick,
    viewReviewTransactionsOnClick,
    viewCardOnClick,
    viewLinkedCardOnClick,
    goTo,
    plan
}: props): JSX.Element {
    const toReview: number =
        (user.expenseTransactionsToReviewCount as number) + (user.incomeTransactionsToReviewCount as number);

    const showPlanActions: boolean =
        toReview > 0 ||
        plan.savingsMissingAccounts.length !== 0 ||
        plan.savingsNotDone.length !== 0 ||
        plan.budgetsClose.length !== 0 ||
        plan.budgetsOver.length !== 0 ||
        plan.disconnectedInstitutions.length !== 0 ||
        plan.dangerString !== undefined;

    return showPlanActions ? (
        <Card>
            <Typography component="h2" variant={TypographyVariants.HEADING_XS}>
                {' '}
                {LiteralsService.getLiteral('Insights.planActions.planActions')}
            </Typography>
            <Typography variant={TypographyVariants.CAPTION}>
                {LiteralsService.getLiteral('Insights.planActions.planActionsSub')}
            </Typography>
            {toReview > 0 && (
                <Section
                    data-cy={Selectors.PlanAhead.PLAN_ACTIONS_REVIEW_TRANSACTIONS_SECTION}
                    icon={
                        <Circle className={bgColors.warningWeak}>
                            <IconSearch size="md" style={{ fill: vars.warningStrong }} />
                        </Circle>
                    }
                    button={
                        <ButtonSecondary
                            className={styles.button}
                            onClick={viewReviewTransactionsOnClick}
                            data-cy="insights-planactions-transactions-button"
                        >
                            {LiteralsService.getLiteral('Insights.planActions.review')}
                        </ButtonSecondary>
                    }
                >
                    <Typography variant={TypographyVariants.SUBTEXT_BOLD}>
                        {LiteralsService.getLiteral('Insights.planActions.transactionsToReview', [toReview])}
                    </Typography>
                    <Typography variant={TypographyVariants.BODY_BOLD}>
                        {LiteralsService.getLiteral('Insights.planActions.transactionsSub')}
                    </Typography>
                </Section>
            )}

            {renderInstitutionWarnings(user.accounts, goTo)}

            {plan.savingsMissingAccounts.map((card: CardModel, index: number) => {
                const linkedCard = CardService.getLinkedCardFromUser(user, card);
                const name = linkedCard !== null ? CardService.getName(linkedCard) : CardService.getName(card);
                const icon = linkedCard !== null ? goalIcons[linkedCard.description] : goalIcons[card.description];

                return (
                    <Section
                        data-cy={Selectors.PlanAhead.PLAN_ACTIONS_SAVINGS_MISSING_ACCOUNTS}
                        key={`missing-${index}`}
                        className={styles.reminders}
                        icon={<Circle className={bgColors.brandWeak}>{icon}</Circle>}
                        button={
                            <ButtonSecondary
                                className={styles.button}
                                onClick={() => {
                                    if (linkedCard !== null) {
                                        viewLinkedCardOnClick(card);
                                    } else {
                                        viewCardOnClick(card);
                                    }
                                }}
                                data-cy="insights-planactions-transactions-button"
                            >
                                {LiteralsService.getLiteral('Insights.planActions.review')}
                            </ButtonSecondary>
                        }
                    >
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            <Anchor
                                onClick={() => {
                                    if (linkedCard !== null) {
                                        viewLinkedCardOnClick(card);
                                    } else {
                                        viewCardOnClick(card);
                                    }
                                }}
                            >
                                {name}
                            </Anchor>
                            {LiteralsService.getLiteral('Insights.planActions.savingsGoalUnattached')}
                        </Typography>
                    </Section>
                );
            })}

            {plan.savingsNotDone.map((card: CardModel, index: number) => {
                const linkedCard: CardModel = CardService.getLinkedCardFromUser(user, card);
                const name: string = linkedCard !== null ? CardService.getName(linkedCard) : CardService.getName(card);
                const icon: JSX.Element =
                    linkedCard !== null ? goalIcons[linkedCard.description] : goalIcons[card.description];
                const lastOfMonth: string = FormatService.formatMonthDay(
                    LocalDate.now().withDayOfMonth(1).plusMonths(1).minusDays(1)
                );

                return (
                    <Section
                        id={`not-done-${index}`}
                        data-cy={Selectors.PlanAhead.PLAN_ACTIONS_SAVINGS_NOT_DONE}
                        key={`not-done-${index}`}
                        className={styles.reminders}
                        icon={<Circle className={bgColors.brandWeak}>{icon}</Circle>}
                        button={
                            <ButtonSecondary
                                className={styles.button}
                                onClick={async () => {
                                    await PlanService.ignoreSavingsGoal(plan, card);
                                    $(`#not-done-${index}`).hide();
                                }}
                                data-cy="insights-planactions-transactions-button"
                            >
                                {LiteralsService.getLiteral('Insights.planActions.dismiss')}
                            </ButtonSecondary>
                        }
                    >
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getJsxLiteral('Insights.planActions.saveToYourPrefix', [
                                card.attributes.perMonth
                            ])}
                            <Anchor
                                onClick={() => {
                                    if (linkedCard !== null) {
                                        viewLinkedCardOnClick(card);
                                    } else {
                                        viewCardOnClick(card);
                                    }
                                }}
                            >
                                {name}
                            </Anchor>
                            {LiteralsService.getJsxLiteral('Insights.planActions.saveToYourSuffix', [lastOfMonth])}
                        </Typography>
                    </Section>
                );
            })}

            {plan.budgetsClose.map((budget: BudgetModel, index: number) => {
                return (
                    <Section
                        data-cy={Selectors.PlanAhead.PLAN_ACTIONS_BUDGETS_CLOSE}
                        key={`budget-close-${index}`}
                        icon={
                            <Circle className={bgColors.warningWeak}>
                                <IconWarning size="md" style={{ fill: vars.warningStrong }} />
                            </Circle>
                        }
                        button={
                            <ButtonSecondary
                                className={styles.button}
                                onClick={() => viewBudgetTransactionsOnClick(budget)}
                            >
                                {LiteralsService.getLiteral('Insights.planActions.viewBudget')}
                            </ButtonSecondary>
                        }
                    >
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('Insights.planActions.closeToOverspending')}
                            <Anchor
                                onClick={() => viewBudgetTransactionsOnClick(budget)}
                                data-cy={Selectors.PlanAhead.INSIGHTS_BUDGET_ANCHOR}
                            >
                                {budget.groupName}
                            </Anchor>
                            {LiteralsService.getLiteral('Insights.planActions.budgetCategory')}
                        </Typography>
                    </Section>
                );
            })}

            {plan.budgetsOver.map((budget: BudgetModel, index: number) => {
                return (
                    <Section
                        data-cy={Selectors.PlanAhead.PLAN_ACTIONS_BUDGETS_OVER}
                        key={`budget-over-${index}`}
                        icon={
                            <Circle className={bgColors.warningWeak}>
                                <IconWarning size="md" style={{ fill: vars.warningStrong }} />
                            </Circle>
                        }
                        button={
                            <ButtonSecondary
                                className={styles.button}
                                onClick={() => viewBudgetTransactionsOnClick(budget)}
                            >
                                {LiteralsService.getLiteral('Insights.planActions.viewBudget')}
                            </ButtonSecondary>
                        }
                    >
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('Insights.planActions.overspending')}
                            <Anchor
                                onClick={() => viewBudgetTransactionsOnClick(budget)}
                                data-cy={Selectors.PlanAhead.INSIGHTS_BUDGET_ANCHOR}
                            >
                                {budget.groupName}
                            </Anchor>
                            {LiteralsService.getLiteral('Insights.planActions.budgetCategory')}
                        </Typography>
                    </Section>
                );
            })}

            {plan.dangerString && (
                <Section
                    data-cy={Selectors.PlanAhead.PLAN_ACTIONS_DANGER_SECTION}
                    key={'danger'}
                    icon={
                        <Circle className={bgColors.warningWeak}>
                            <IconWarning size="md" style={{ fill: vars.warningStrong }} />
                        </Circle>
                    }
                    button={
                        <ButtonSecondary className={styles.button} onClick={(): void => goTo('/goals')}>
                            {LiteralsService.getLiteral('Insights.planActions.editLifeEvents')}
                        </ButtonSecondary>
                    }
                >
                    <Typography variant={TypographyVariants.BODY_BOLD}>
                        {LiteralsService.getLiteral('Insights.planActions.dangerString', [plan.dangerString])}
                    </Typography>
                    <Typography variant={TypographyVariants.SUBTEXT}>
                        {LiteralsService.getLiteral('Insights.planActions.dangerString2')}
                    </Typography>
                </Section>
            )}
        </Card>
    ) : null;
}
